import React from 'react';
import Header from 'src/layouts/Header';
import Footer from 'src/layouts/Footer';
import useSEO from 'src/hooks/useSEO';

const TermsAndConditions = () => {
  useSEO({
    title: 'Terms and Conditions - SphynxPlace.com',
    description: 'Read SphynxPlace.com terms and conditions to understand the rules and guidelines for using our platform.',
    robots: 'index, follow',
  });
  return (
    <>
    <Header />
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-3xl md:text-4xl font-bold mb-8">Terms & Conditions</h1>
      
      <div className="mb-6 text-gray-600">
        <p><strong>Effective date:</strong> 1.2.2026</p>
        <p><strong>Website:</strong> sphynxplace.com</p>
        <p><strong>Contact:</strong> <a href="mailto:support@sphynxplace.com" className="text-blue-600 hover:underline">support@sphynxplace.com</a></p>
      </div>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Acceptance of Terms</h2>
        <p className="mb-4">
          By accessing or using SphynxPlace (the "Platform"), you confirm that you have read, understood, and agreed to be bound by these Terms & Conditions. If you do not agree, you must not use the Platform.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Platform Role and Disclaimer</h2>
        <p className="mb-4">
          SphynxPlace operates solely as an online marketplace and communication platform that allows users to post listings and contact each other. We are not a breeder, seller, buyer, adopter, transporter, payment processor, escrow service, or veterinary provider.
        </p>
        <p className="mb-4">
          SphynxPlace is not a party to any agreement, transaction, or communication between users. All transactions, negotiations, payments, deliveries, adoptions, and agreements are conducted solely between users at their own risk.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">User Eligibility</h2>
        <p className="mb-4">
          The Platform is intended only for individuals who are at least 16 years of age. By using the Platform, you confirm that you meet this requirement and have the legal capacity to enter into binding agreements where applicable.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">User Accounts</h2>
        <p className="mb-4">
          Users may be required to create an account to access certain features. You are responsible for providing accurate information, maintaining the confidentiality of your login credentials, and all activity conducted under your account.
        </p>
        <p className="mb-4">
          SphynxPlace reserves the right to suspend or terminate accounts that violate these Terms, provide false information, or engage in prohibited activities.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Listings and User Content</h2>
        <p className="mb-4">
          Users are solely responsible for all content they post, including listings, photos, descriptions, prices, locations, health information, pedigree claims, and documentation.
        </p>
        <p className="mb-4">
          SphynxPlace does not verify, endorse, or guarantee the accuracy, completeness, or legality of any listing or user-submitted content. Appearance, health, temperament, registration status, and condition of cats may vary, and descriptions are provided by users "as is."
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">No Warranty and Health Disclaimer</h2>
        <p className="mb-4">
          All listings and services on the Platform are provided on an "as is" and "as available" basis. SphynxPlace makes no warranties, express or implied, regarding:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-2">
          <li>the health, genetics, temperament, age, appearance, or condition of any cat</li>
          <li>the accuracy of health records, vaccination status, pedigree certificates, or breeder claims</li>
          <li>outcomes of any purchase, adoption, or breeding arrangement</li>
        </ul>
        <p className="mb-4">
          Users are strongly encouraged to consult licensed veterinarians and conduct independent inspections before completing any transaction.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Payments and Transactions</h2>
        <p className="mb-4">
          SphynxPlace does not process, hold, guarantee, or mediate payments. Any deposits, fees, or payments are made directly between users.
        </p>
        <p className="mb-4">
          We are not responsible for payment disputes, chargebacks, delivery failures, scams, or financial losses arising from user transactions.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Prohibited Activities</h2>
        <p className="mb-4">Users agree not to:</p>
        <ul className="list-disc pl-6 mb-4 space-y-2">
          <li>post false, misleading, or fraudulent listings</li>
          <li>violate applicable laws or animal welfare regulations</li>
          <li>post prohibited, offensive, or abusive content</li>
          <li>attempt to bypass platform safeguards</li>
          <li>misuse personal data of other users</li>
        </ul>
        <p className="mb-4">
          SphynxPlace reserves the right to remove content and restrict access at its sole discretion.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Reporting and Moderation</h2>
        <p className="mb-4">
          Users may report listings or accounts believed to violate these Terms. While we may review reports, SphynxPlace is not obligated to investigate or take action and does not guarantee the removal of reported content.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Limitation of Liability</h2>
        <p className="mb-4">
          To the maximum extent permitted by law, SphynxPlace, its owners, operators, affiliates, and partners shall not be liable for any direct, indirect, incidental, consequential, or special damages arising from:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-2">
          <li>use of or inability to use the Platform</li>
          <li>interactions or transactions between users</li>
          <li>inaccuracies in listings or user content</li>
          <li>health, injury, illness, death, or defects of any animal</li>
          <li>financial loss, emotional distress, or reputational harm</li>
        </ul>
        <p className="mb-4">
          Use of the Platform is entirely at your own risk.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Indemnification</h2>
        <p className="mb-4">
          You agree to indemnify and hold harmless SphynxPlace from any claims, damages, losses, liabilities, costs, or expenses arising from your use of the Platform, your listings, your interactions with other users, or your violation of these Terms.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Intellectual Property</h2>
        <p className="mb-4">
          All Platform content, trademarks, branding, and design elements belong to SphynxPlace or its licensors. Users retain ownership of their own content but grant SphynxPlace a non-exclusive, worldwide license to display and distribute it on the Platform.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Third-Party Links</h2>
        <p className="mb-4">
          The Platform may contain links to third-party websites or services. SphynxPlace is not responsible for third-party content, policies, or practices.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Termination</h2>
        <p className="mb-4">
          SphynxPlace may suspend or terminate access to the Platform at any time, with or without notice, if these Terms are violated or for operational or legal reasons.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Changes to Terms</h2>
        <p className="mb-4">
          We reserve the right to update or modify these Terms at any time. Continued use of the Platform after changes are posted constitutes acceptance of the updated Terms.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Governing Law and Jurisdiction</h2>
        <p className="mb-4">
          These Terms shall be governed by and interpreted in accordance with the laws applicable at the place of the Platform operator. Any disputes shall be subject to the exclusive jurisdiction of the competent courts at that location.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Contact Information</h2>
        <p className="mb-4">For questions regarding these Terms, please contact:</p>
        <p>
          <a href="mailto:support@sphynxplace.com" className="text-blue-600 hover:underline">support@sphynxplace.com</a>
        </p>
      </section>
    </div>
    <Footer />
    </>
  );
};

export default TermsAndConditions;